/**
 * @fileoverview Assurance Actions Service for managing assurance tasks
 * This service provides a facade over the Thalassa API
 * to simplify assurance task operations throughout the application.
 * 
 * Migrated from direct Firestore to Thalassa API.
 */
import { auth } from '../js/external-js/config/main_config';
import { deiterateAPI } from '../js/external-js/variableModules/utils/deiterateAPI.util';

const ASSURANCE_API = '/v1/assurance';

// Use Window interface from TenancyContext

/** Type definitions for parameters */
export interface TaskFilter {
  standard?: string;
  overdue?: boolean;
  dueSoon?: boolean;
  assignee?: string;
  startDate?: Date;
  endDate?: Date;
  includeDeleted?: boolean;
}

export interface AssuranceTask {
  taskCreationMetadata?: {
    createdBy: string;
    createdAt: Date | any;
    updatedAt: Date | any;
  };
  // Soft delete fields
  isDeleted?: boolean;
  deletedAt?: Date | any;
  deletedBy?: string;
  checkDetail: {
    active: string;
    assignedTo: string;
    check: string;
    control?: string;
    evidence: string;
    framework: Record<string, { frameName: string; controls: string[] }>;
    meetingNeeded?: string;
    frameworkSkipped?: boolean; // Add support for intentionally skipping framework selection
  };
  checkFrequency: {
    checkDays: string;
    lastCheck?: Record<string, { checkDate: any }>;
    nextCheck?: any;
    reminderDays: string;
    sendReminder: string;
    performedDate?: Date;
  };
  checkHistory?: Record<string, any>;
  history?: Record<string, {
    changeID: string;
    changedBy: string;
    dateChanged: Date;
    fieldsChanged: Record<string, {
      dbFieldName: string;
      fieldName: string;
      newValue: any;
      oldValue: any;
    }>;
  }>;
  // Additional properties for compatibility
  assID?: string;
  taskOwner?: string;
  nextReviewDate?: Date;  // Mapped to checkFrequency.nextCheck
  taskHistory?: Array<{
    changedBy: string;
    changedDate: Date;
    changes: Record<string, any>;
  }>;
  // Additional properties for task performance
  evidence?: any[];
  comments?: string;
  dataClassification?: string;
}

/**
 * Retrieves all assurance tasks from the system
 * 
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to an array of tasks
 */
export const getAllTasks = async (customerID?: string, includeDeleted: boolean = false): Promise<AssuranceTask[]> => {
  try {
    console.log('[AssuranceService] Getting all tasks');
    const companyId = customerID !== undefined ? customerID : window.companyID;
    
    const queryParams = includeDeleted ? '?includeDeleted=true' : '';
    const res = await deiterateAPI.GET(`${ASSURANCE_API}${queryParams}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.tasks) {
      console.log(`[AssuranceService] Found ${res.tasks.length} tasks`);
      return res.tasks;
    } else {
      console.log('[AssuranceService] No tasks found');
      return [];
    }
  } catch (error) {
    console.error('[AssuranceService] Error getting all tasks:', error);
    return [];
  }
};

/**
 * Retrieves tasks for a specific standard
 * 
 * @param {string} standard - The standard to filter by (ISO27001, Essential 8, etc.)
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to an array of filtered tasks
 */
export const getTasksByStandard = async (standard: string): Promise<AssuranceTask[]> => {
  try {
    console.log(`[AssuranceService] Getting tasks for standard: ${standard}`);
    const companyId = window.companyID;
    
    const res = await deiterateAPI.GET(`${ASSURANCE_API}/standard/${encodeURIComponent(standard)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.tasks) {
      console.log(`[AssuranceService] Found ${res.tasks.length} tasks for ${standard}`);
      return res.tasks;
    } else {
      console.log('[AssuranceService] No tasks found');
      return [];
    }
  } catch (error) {
    console.error(`[AssuranceService] Error getting tasks for ${standard}:`, error);
    return [];
  }
};

/**
 * Retrieves a specific task by its ID
 * 
 * @param {string} taskId - The task ID to search for
 * @returns {Promise<AssuranceTask | undefined>} A promise that resolves to the found task or undefined
 */
export const getTask = async (taskId: string): Promise<AssuranceTask | undefined> => {
  try {
    console.log('[AssuranceService] getTask', taskId);
    const companyId = window.companyID;
    
    const res = await deiterateAPI.GET(`${ASSURANCE_API}/${encodeURIComponent(taskId)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.task) {
      console.log('[AssuranceService] Found task:', taskId);
      return res.task;
    } else {
      console.log('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error getting task:', error);
    return undefined;
  }
};

/**
 * Creates a new assurance task
 * 
 * @param {Partial<AssuranceTask>} taskData - The task data to create
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const createTask = async (
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] Creating new task');
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.POST(ASSURANCE_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
        'X-User-Id': auth.currentUser?.uid || '',
      },
      body: JSON.stringify(taskData)
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task created successfully with ID:', res.id);
      return {
        success: true,
        id: res.id
      };
    } else {
      console.error('[AssuranceService] Error creating task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error creating task:', error);
    return undefined;
  }
};

/**
 * Updates an existing assurance task
 * 
 * @param {string} taskId - The ID of the task to update
 * @param {Partial<AssuranceTask>} taskData - The task data to update
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const updateTask = async (
  taskId: string,
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] updateTask', taskId);
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.PUT(`${ASSURANCE_API}/${encodeURIComponent(taskId)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
        'X-User-Id': auth.currentUser?.uid || '',
      },
      body: JSON.stringify(taskData)
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task updated successfully:', taskId);
      return {
        success: true,
        id: taskId
      };
    } else {
      console.error('[AssuranceService] Error updating task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error updating task:', error);
    return undefined;
  }
};

/**
 * Performs (completes) a task
 * 
 * @param {string} taskId - The ID of the task to perform
 * @param {Partial<AssuranceTask>} taskData - Additional data for the task performance
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const performTask = async (
  taskId: string,
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] performTask', taskId);
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.POST(`${ASSURANCE_API}/${encodeURIComponent(taskId)}/perform`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
        'X-User-Id': auth.currentUser?.uid || '',
      },
      body: JSON.stringify({
        performedDate: taskData.checkFrequency?.performedDate || new Date(),
        assignedTo: taskData.checkDetail?.assignedTo,
        comments: taskData.comments || '',
        dataClassification: taskData.dataClassification || 'Confidential',
        evidence: taskData.evidence || []
      })
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task performed successfully:', taskId);
      return {
        success: true,
        id: taskId
      };
    } else {
      console.error('[AssuranceService] Error performing task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error performing task:', error);
    return undefined;
  }
};

/**
 * Soft deletes an assurance task (marks as deleted but keeps data)
 * 
 * @param {string} taskId - The ID of the task to soft delete
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const softDeleteTask = async (
  taskId: string
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] softDeleteTask', taskId);
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.POST(`${ASSURANCE_API}/${encodeURIComponent(taskId)}/soft-delete`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
        'X-User-Id': auth.currentUser?.uid || '',
      }
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task soft deleted successfully:', taskId);
      return { success: true, id: taskId };
    } else {
      console.error('[AssuranceService] Error soft deleting task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error soft deleting task:', error);
    return undefined;
  }
};

/**
 * Restores a soft deleted assurance task
 * 
 * @param {string} taskId - The ID of the task to restore
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const restoreTask = async (
  taskId: string
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] restoreTask', taskId);
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.POST(`${ASSURANCE_API}/${encodeURIComponent(taskId)}/restore`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task restored successfully:', taskId);
      return { success: true, id: taskId };
    } else {
      console.error('[AssuranceService] Error restoring task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error restoring task:', error);
    return undefined;
  }
};

/**
 * Deletes an assurance task
 * 
 * @param {string} taskId - The ID of the task to delete
 * @returns {Promise<{success: boolean} | undefined>} A promise that resolves to the API response
 */
export const deleteTask = async (
  taskId: string
): Promise<{success: boolean} | undefined> => {
  console.log('[AssuranceService] deleteTask', taskId);
  
  try {
    const companyId = window.companyID;
    
    const res = await deiterateAPI.DELETE(`${ASSURANCE_API}/${encodeURIComponent(taskId)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.success) {
      console.log('[AssuranceService] Task deleted successfully:', taskId);
      return { success: true };
    } else {
      console.error('[AssuranceService] Error deleting task:', res.error);
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error deleting task:', error);
    return undefined;
  }
};

/**
 * Gets the task history for a specific assurance task
 * 
 * @param {string} taskId - The ID of the task to get history for
 * @returns {Promise<Array<{changedBy: string; changedDate: Date; changes: Record<string, any>}>>} A promise that resolves to the task history
 */
export const getTaskHistory = async (
  taskId: string
): Promise<Array<{changedBy: string; changedDate: Date; changes: Record<string, any>}>> => {
  try {
    console.log('[AssuranceService] Getting task history for', taskId);
    const companyId = window.companyID;
    
    const res = await deiterateAPI.GET(`${ASSURANCE_API}/${encodeURIComponent(taskId)}/history`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.history) {
      console.log(`[AssuranceService] Found ${res.history.length} history entries`);
      return res.history;
    }
    
    console.log('[AssuranceService] No history found for task');
    return [];
  } catch (error) {
    console.error('[AssuranceService] Error getting task history:', error);
    return [];
  }
};

/**
 * Gets filtered tasks based on provided criteria
 * 
 * @param {TaskFilter} filter - Filter criteria including standard, overdue status, etc.
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to filtered tasks
 */
export const getFilteredTasks = async (
  filter: TaskFilter
): Promise<AssuranceTask[]> => {
  try {
    console.log('[AssuranceService] Getting filtered tasks', filter);
    const companyId = window.companyID;
    
    // Build query params
    const params = new URLSearchParams();
    if (filter.standard) params.append('standard', filter.standard);
    if (filter.overdue) params.append('overdue', 'true');
    if (filter.dueSoon) params.append('dueSoon', 'true');
    if (filter.assignee) params.append('assignee', filter.assignee);
    if (filter.includeDeleted) params.append('includeDeleted', 'true');
    
    const queryString = params.toString();
    const url = `${ASSURANCE_API}/filtered${queryString ? `?${queryString}` : ''}`;
    
    const res = await deiterateAPI.GET(url, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyId,
        'Company-ID': companyId,
      }
    });
    
    if (res.tasks) {
      console.log(`[AssuranceService] Found ${res.tasks.length} tasks after filtering`);
      return res.tasks;
    }
    
    return [];
  } catch (error) {
    console.error('[AssuranceService] Error filtering tasks:', error);
    return [];
  }
};

/** Default export of all assurance service functions */
export default {
  getAllTasks,
  getTasksByStandard,
  getTask,
  createTask,
  updateTask,
  performTask,
  deleteTask,
  softDeleteTask,
  restoreTask,
  getTaskHistory,
  getFilteredTasks,
};
